import { z } from 'zod'

/**
 * How fast the public API (clipbook-api) answers its clients, for Clipbook staff (CLI-1041). Every figure is
 * clipbook-api's own (`GET /internal/latency`, read raw from its usage events): `latency_ms` runs from a
 * request entering the `/v1/*` or `/mcp` chain until its response is BUILT — auth, Redis, every ClickHouse
 * read and the JSON — not the network time to the client. Errors stay in the quantiles (a timeout is latency
 * the client felt); 5xx and timeouts are counted beside them.
 *
 * The page is internal: never a client's view of its own API (that is the workspace API page, CLI-1036).
 */

/** clipbook-api's two metered surfaces: REST (`/v1/*`) and MCP (`/mcp`). */
export const API_LATENCY_SURFACES = ['rest', 'mcp'] as const
export type ApiLatencySurface = (typeof API_LATENCY_SURFACES)[number]

/** How the trend is bucketed. clipbook-api also has `all` (one row per operation), which the routes ask for. */
export const API_LATENCY_BUCKETS = ['hour', 'day'] as const
export type ApiLatencyBucket = (typeof API_LATENCY_BUCKETS)[number]

/** clipbook-api's read limits: hourly buckets over at most 31 days, anything else over at most 400. */
export const API_LATENCY_HOURLY_MAX_DAYS = 31
export const API_LATENCY_MAX_DAYS = 400

/** The longest operation name clipbook-api accepts as a filter. */
export const API_LATENCY_OPERATION_MAX_LENGTH = 100

/** The summary compares a range with the seven days before it. */
export const API_LATENCY_BASELINE_DAYS = 7

/**
 * Fewer requests than this and a percentile is an anecdote: a p95 of 12 requests is the second-slowest one.
 * The page greys such rows (and their deltas) rather than hiding them.
 */
export const API_LATENCY_MIN_REQUESTS = 30

export function tooFewForPercentiles(requests: number): boolean {
  return requests < API_LATENCY_MIN_REQUESTS
}

const MS_PER_DAY = 86_400_000

const spanDays = (range: { from: string; to: string }) =>
  (Date.parse(range.to) - Date.parse(range.from)) / MS_PER_DAY

const filterFields = {
  from: z.iso.datetime(),
  /** Exclusive. */
  to: z.iso.datetime(),
  surface: z.enum(API_LATENCY_SURFACES).optional(),
  /** An operation as clipbook-api names it (`search_records`, `mcp.tools/list`, …). */
  operation: z.string().trim().min(1).max(API_LATENCY_OPERATION_MAX_LENGTH).optional(),
  endpointId: z.uuid().optional(),
}

/** The filters every latency read takes: a range of at most 400 days, and optionally one surface, operation or endpoint. */
export const apiLatencyFilterSchema = z
  .object(filterFields)
  .refine((range) => spanDays(range) > 0, { message: 'to must be after from' })
  .refine((range) => spanDays(range) <= API_LATENCY_MAX_DAYS, {
    message: `At most ${API_LATENCY_MAX_DAYS} days`,
  })
export type ApiLatencyFilter = z.infer<typeof apiLatencyFilterSchema>

/** The trend's filters: the same, bucketed by hour (at most 31 days) or day. */
export const apiLatencyTrendQuerySchema = z
  .object({ ...filterFields, bucket: z.enum(API_LATENCY_BUCKETS).default('hour') })
  .refine((range) => spanDays(range) > 0, { message: 'to must be after from' })
  .refine((range) => spanDays(range) <= API_LATENCY_MAX_DAYS, {
    message: `At most ${API_LATENCY_MAX_DAYS} days`,
  })
  .refine((query) => query.bucket !== 'hour' || spanDays(query) <= API_LATENCY_HOURLY_MAX_DAYS, {
    message: `Hourly buckets cover at most ${API_LATENCY_HOURLY_MAX_DAYS} days: use day`,
  })
export type ApiLatencyTrendQuery = z.infer<typeof apiLatencyTrendQuerySchema>

const count = z.number().int().nonnegative()
const ms = z.number().nonnegative()

/**
 * One row of clipbook-api's `GET /internal/latency`, parsed at the boundary. `bucket_start` is null in
 * revision mode, where `revision` and its `first_seen` / `last_seen` are set; `revision` is `''` for events
 * written before the column existed.
 */
export const clipbookApiLatencyRowSchema = z.object({
  bucket_start: z.string().nullable(),
  revision: z.string().nullable(),
  first_seen: z.string().nullable().optional(),
  last_seen: z.string().nullable().optional(),
  surface: z.string(),
  operation: z.string(),
  requests: count,
  avg_ms: ms,
  p50_ms: ms,
  p95_ms: ms,
  p99_ms: ms,
  max_ms: ms,
  /** 5xx, or `error_code` in (`internal_error`, `unavailable`): the SLA's rule. */
  server_errors: count,
  /** `http_status >= 400 OR error_code != ''`: the client's own 401s and 429s too. */
  error_requests: count,
  /** Server errors that ran to the 59 s mark. */
  timeouts: count,
})
export type ClipbookApiLatencyRow = z.infer<typeof clipbookApiLatencyRowSchema>

export const clipbookApiLatencyResponseSchema = z.object({
  from: z.string(),
  to: z.string(),
  bucket: z.enum(['hour', 'day', 'all']),
  group: z.string().nullable().optional(),
  rows: z.array(clipbookApiLatencyRowSchema),
})
export type ClipbookApiLatencyResponse = z.infer<typeof clipbookApiLatencyResponseSchema>

/** One operation's latency over some span, in milliseconds. */
export const apiLatencyStatsSchema = z.object({
  requests: z.number().int(),
  avgMs: z.number(),
  p50Ms: z.number(),
  p95Ms: z.number(),
  p99Ms: z.number(),
  maxMs: z.number(),
  serverErrors: z.number().int(),
  errorRequests: z.number().int(),
  timeouts: z.number().int(),
})
export type ApiLatencyStats = z.infer<typeof apiLatencyStatsSchema>

const operationKey = { surface: z.string(), operation: z.string() }

/**
 * The range against the seven days before it, per operation. `delta` is range minus baseline (ms, and the
 * p95's change as a share of the baseline's); null when either side has no requests.
 */
export const apiLatencySummarySchema = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  baseline: z.object({ from: z.iso.datetime(), to: z.iso.datetime() }),
  asOf: z.iso.datetime(),
  operations: z.array(
    z.object({
      ...operationKey,
      range: apiLatencyStatsSchema.nullable(),
      baseline: apiLatencyStatsSchema.nullable(),
      delta: z
        .object({
          p50Ms: z.number(),
          p95Ms: z.number(),
          p99Ms: z.number(),
          p95Percent: z.number().nullable(),
        })
        .nullable(),
    }),
  ),
})
export type ApiLatencySummary = z.infer<typeof apiLatencySummarySchema>

/** Per operation and bucket (hour or day, UTC), oldest first. */
export const apiLatencyTrendSchema = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  bucket: z.enum(API_LATENCY_BUCKETS),
  asOf: z.iso.datetime(),
  points: z.array(
    z.object({ bucketStart: z.string(), ...operationKey, ...apiLatencyStatsSchema.shape }),
  ),
})
export type ApiLatencyTrend = z.infer<typeof apiLatencyTrendSchema>

/**
 * Per serving Cloud Run revision and operation, newest revision first: how a deploy moved latency (12
 * revisions in 48 h made deploy markers useless). `revision` is null for events from before it was recorded.
 */
export const apiLatencyRevisionsSchema = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  asOf: z.iso.datetime(),
  revisions: z.array(
    z.object({
      revision: z.string().nullable(),
      firstSeen: z.string().nullable(),
      lastSeen: z.string().nullable(),
      ...operationKey,
      ...apiLatencyStatsSchema.shape,
    }),
  ),
})
export type ApiLatencyRevisions = z.infer<typeof apiLatencyRevisionsSchema>

/**
 * The endpoints the page's filter offers: those of accounts this caller may see, by name, with whose
 * workspace each is (null for an endpoint in none). The SPA labels a workspace as everywhere else.
 */
export const apiLatencyEndpointsSchema = z.object({
  endpoints: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      workspace: z
        .object({ accountName: z.string(), subaccountName: z.string(), isPrimary: z.boolean() })
        .nullable(),
    }),
  ),
})
export type ApiLatencyEndpoints = z.infer<typeof apiLatencyEndpointsSchema>
